import { IDropdownOption } from '@fluentui/react';
import { FilterArrayType } from 'common/components/Filters';
import { PurchaseOrdersOrderBy } from 'common/types/globalTypes';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import { ToNullable } from 'utility/types';
import { PurchaseOrderSearchFilterTotalsVariables } from './__generated__/PurchaseOrderSearchFilterTotals';

type OrderLookup = {
  [direction in OrderDirection]: PurchaseOrdersOrderBy[];
};

const orderByMap = new Map<string, OrderLookup>([
  [
    'purchaseOrderType',
    {
      [OrderDirection.ASC]: [
        'PURCHASE_ORDER_TYPE_BY_PURCHASE_ORDER_TYPE_ID__PURCHASE_ORDER_TYPE_ASC',
      ],
      [OrderDirection.DESC]: [
        'PURCHASE_ORDER_TYPE_BY_PURCHASE_ORDER_TYPE_ID__PURCHASE_ORDER_TYPE_DESC',
      ],
    },
  ],
  [
    'purchaseOrderNumber',
    {
      [OrderDirection.ASC]: ['PURCHASE_ORDER_NUMBER_ASC'],
      [OrderDirection.DESC]: ['PURCHASE_ORDER_NUMBER_DESC'],
    },
  ],
  [
    'purchaseOrderDate',
    {
      [OrderDirection.ASC]: ['PURCHASE_ORDER_DATE_ASC'],
      [OrderDirection.DESC]: ['PURCHASE_ORDER_DATE_DESC'],
    },
  ],
  [
    'sortSupplierName',
    {
      [OrderDirection.ASC]: ['SORT_SUPPLIER_NAME_ASC'],
      [OrderDirection.DESC]: ['SORT_SUPPLIER_NAME_DESC'],
    },
  ],
  [
    'description',
    {
      [OrderDirection.ASC]: ['DESCRIPTION_ASC'],
      [OrderDirection.DESC]: ['DESCRIPTION_DESC'],
    },
  ],
  [
    'department',
    {
      [OrderDirection.ASC]: ['COMPANY_DEPARTMENT_BY_DEPARTMENT_ID__NAME_ASC'],
      [OrderDirection.DESC]: ['COMPANY_DEPARTMENT_BY_DEPARTMENT_ID__NAME_DESC'],
    },
  ],
  [
    'businessUnit',
    {
      [OrderDirection.ASC]: [
        'COMPANY_BUSINESS_UNIT_BY_BUSINESS_UNIT_ID__NAME_ASC',
      ],
      [OrderDirection.DESC]: [
        'COMPANY_BUSINESS_UNIT_BY_BUSINESS_UNIT_ID__NAME_DESC',
      ],
    },
  ],
  [
    'currency',
    {
      [OrderDirection.ASC]: ['COMPANY_CURRENCY_BY_CURRENCY_ID__ISO_CODE_ASC'],
      [OrderDirection.DESC]: ['COMPANY_CURRENCY_BY_CURRENCY_ID__ISO_CODE_DESC'],
    },
  ],
  [
    'controlTotalAmount',
    {
      [OrderDirection.ASC]: ['CONTROL_TOTAL_AMOUNT_ASC'],
      [OrderDirection.DESC]: ['CONTROL_TOTAL_AMOUNT_DESC'],
    },
  ],
  [
    '_referenceItemTypes',
    {
      [OrderDirection.ASC]: ['_REFERENCE_ITEM_TYPES_ASC'],
      [OrderDirection.DESC]: ['_REFERENCE_ITEM_TYPES_DESC'],
    },
  ],
  [
    '_accountingStampTransactionReference',
    {
      [OrderDirection.ASC]: ['_ACCOUNTING_STAMP_TRANSACTION_REFERENCE_ASC'],
      [OrderDirection.DESC]: ['_ACCOUNTING_STAMP_TRANSACTION_REFERENCE_DESC'],
    },
  ],
  [
    '_createdByUserName',
    {
      [OrderDirection.ASC]: ['_CREATED_BY_USER_NAME_ASC'],
      [OrderDirection.DESC]: ['_CREATED_BY_USER_NAME_DESC'],
    },
  ],
  [
    '_createdDate',
    {
      [OrderDirection.ASC]: ['_CREATED_DATE_ASC'],
      [OrderDirection.DESC]: ['_CREATED_DATE_DESC'],
    },
  ],
  [
    '_baseCurrencyAmount',
    {
      [OrderDirection.ASC]: ['_BASE_CURRENCY_AMOUNT_ASC'],
      [OrderDirection.DESC]: ['_BASE_CURRENCY_AMOUNT_DESC'],
    },
  ],
  [
    '_spotCurrencyAmount',
    {
      [OrderDirection.ASC]: ['_SPOT_CURRENCY_AMOUNT_ASC'],
      [OrderDirection.DESC]: ['_SPOT_CURRENCY_AMOUNT_DESC'],
    },
  ],
  [
    'status',
    {
      [OrderDirection.ASC]: ['STATUS_TYPE_BY_STATUS_TYPE_ID__STATUS_TYPE_ASC'],
      [OrderDirection.DESC]: [
        'STATUS_TYPE_BY_STATUS_TYPE_ID__STATUS_TYPE_DESC',
      ],
    },
  ],
]);

export const toOrderByVariable = (
  sort?: SortOrder
): PurchaseOrdersOrderBy[] => {
  const orderByArray: PurchaseOrdersOrderBy[] = [
    'PURCHASE_ORDER_NUMBER_DESC',
    'PRIMARY_KEY_ASC',
  ];

  if (!sort) return orderByArray;

  const orderBy = orderByMap.get(sort.column)?.[sort.direction];
  return [...(orderBy ?? []), 'PRIMARY_KEY_ASC'];
};

export const toFilterVariable = (filterList: FilterArrayType[]) =>
  filterList
    .map((filter) => {
      switch (filter.filterKey) {
        case '_accountingExportCycleId':
          const isFilterUnprocessedDocuments = filterList.find(
            (filter) => filter.filterKey === 'isFilterUnprocessedDocuments'
          );
          if (!isFilterUnprocessedDocuments?.value) {
            return {
              [filter.filterKey]: {
                equalTo: filter.value,
              },
            };
          }
          return undefined;
        case 'filterSupplierId':
        case 'purchaseOrderTypeId':
        case 'departmentId':
        case 'businessUnitId':
        case 'currencyId':
        case '_createdByUserid':
          return {
            [filter.filterKey]: {
              equalTo: filter.value,
            },
          };
        default:
          return undefined;
      }
    })
    .filter((item) => item !== undefined);

export const toFilterVariableExport = (filterList: FilterArrayType[]) => {
  const keyMapping: { [key: string]: string } = {
    purchaseOrderTypeId: 'filterPurchaseOrderTypeId',
    departmentId: 'filterDepartmentId',
    businessUnitId: 'filterBusinessUnitId',
    currencyId: 'filterCurrencyId',
    filterMultiStatusTypes: 'filterMultiStatusTypes',
    _createdByUserid: 'filterCreatedById',
    filterSupplierId: 'filterSupplierId',
    _accountingExportCycleId: 'filterProcessHistoryId',
    isFilterUnprocessedDocuments: 'isFilterUnprocessedDocuments',
  };

  const filterVariablesList = filterList.reduce((acc, { filterKey, value }) => {
    const newKey = keyMapping[filterKey];
    if (newKey) {
      acc[newKey] = value;
    }
    return acc;
  }, {} as { [key: string]: string | number | boolean | any[] });
  return filterVariablesList;
};

type searchTotalFilterType = ToNullable<
  Omit<
    PurchaseOrderSearchFilterTotalsVariables,
    | 'isSigningSearch'
    | 'isHistorySearch'
    | 'searchRequest'
    | 'isLinkSearch'
    | 'isSearchAllData'
  >
>;
export const getFilterParams = (searchFilters: FilterArrayType[]) => {
  const searchTotalFilterArray: searchTotalFilterType = {
    filterCurrencyId: undefined,
    filterCreatedById: undefined,
    filterPurchaseOrderTypeId: undefined,
    filterSupplierId: undefined,
    filterBusinessUnitId: undefined,
    filterDepartmentId: undefined,
    filterMultiStatusTypes: undefined,
  };
  searchFilters.forEach((curr) => {
    switch (curr.filterKey) {
      case 'filterSupplierId':
        return (searchTotalFilterArray['filterSupplierId'] =
          curr.value as GqlUUID | null);
      case 'purchaseOrderTypeId':
        return (searchTotalFilterArray['filterPurchaseOrderTypeId'] =
          curr.value as number | null);
      case 'businessUnitId':
        return (searchTotalFilterArray['filterBusinessUnitId'] =
          curr.value as GqlUUID | null);
      case 'departmentId':
        return (searchTotalFilterArray['filterDepartmentId'] =
          curr.value as GqlUUID | null);
      case 'filterMultiStatusTypes':
        return (searchTotalFilterArray['filterMultiStatusTypes'] =
          curr.value as number[] | null);
      case 'currencyId':
        return (searchTotalFilterArray['filterCurrencyId'] = curr.value as
          | number
          | null);
      case '_createdByUserid':
        return (searchTotalFilterArray['filterCreatedById'] = curr.value as
          | string
          | null);
      case '_accountingExportCycleId':
        const isFilterUnprocessedDocuments = searchFilters.find(
          (filter) => filter.filterKey === 'isFilterUnprocessedDocuments'
        );
        if (isFilterUnprocessedDocuments?.value) {
          return (searchTotalFilterArray['filterProcessHistoryId'] = undefined);
        }
        return (searchTotalFilterArray['filterProcessHistoryId'] =
          curr.value as (string | null)[] | null | undefined);
      default:
        return searchTotalFilterArray;
    }
  });
  return searchTotalFilterArray;
};

export enum listTypeEnum {
  all = 'ALL_TRANSACTIONS',
  my = 'MY_TRANSACTIONS',
}

export const listTypeOptions: IDropdownOption[] = [
  {
    key: listTypeEnum.my,
    text: 'My Transactions',
  },
  {
    key: listTypeEnum.all,
    text: 'All Transactions',
  },
];
